/**
 * 远程会话信息状态管理 Store。
 */
import { create } from "zustand";

export type DeviceInfo = {
	host_name?: string;
	hostname?: string;
	platform?: string;
	platform_version?: string;
	resolution?: string;
	[key: string]: unknown;
};

export type SessionInfo = {
	ipv4?: string;
	city?: string;
	country?: string;
	location?: string;
	ip?: string;
	ip_address?: string;
	[key: string]: unknown;
};

export type SessionState = {
	deviceId: string;
	passCode: string;
	deviceInfo: DeviceInfo | null;
	sessionInfo: SessionInfo | null;
	expiresAt: string;
	rootPath: string;
};

type SessionStoreState = SessionState & {
	setSessionPayload: (payload: Partial<SessionState>) => void;
	setRootPath: (rootPath: string) => void;
	clearSession: () => void;
};

/**
 * 安全读取 sessionStorage 的字符串值。
 * @param {string} key
 * @param {string} fallback
 */
const readSessionValue = (key: string, fallback = "") => {
	if (typeof window === "undefined") return fallback;
	return sessionStorage.getItem(key) || fallback;
};

/**
 * 安全解析 JSON 字符串。
 * @param {string} value
 * @returns {object|null}
 */
const safeParseJson = <T extends Record<string, unknown>>(value: string) => {
	if (!value) return null;
	try {
		return JSON.parse(value) as T;
	} catch (error) {
		console.warn("Failed to parse session JSON", error);
		return null;
	}
};

/**
 * 构建会话初始状态。
 */
const createInitialSessionState = (): SessionState => ({
	deviceId: readSessionValue("current_device_id"),
	passCode: readSessionValue("current_device_code"),
	deviceInfo: safeParseJson<DeviceInfo>(readSessionValue("device_info")),
	sessionInfo: safeParseJson<SessionInfo>(readSessionValue("session_info")),
	expiresAt: readSessionValue("session_expires_at"),
	rootPath: readSessionValue("root_info"),
});

/**
 * 构建清理后的会话状态。
 */
const createEmptySessionState = (): SessionState => ({
	deviceId: "",
	passCode: "",
	deviceInfo: null,
	sessionInfo: null,
	expiresAt: "",
	rootPath: "",
});

const useSessionStore = create<SessionStoreState>((set) => ({
	...createInitialSessionState(),
	/**
	 * 更新会话信息字段。
	 * @param {object} payload
	 */
	setSessionPayload: (payload) =>
		set((state) => ({
			...state,
			...payload,
		})),
	/**
	 * 更新远端根目录路径。
	 * @param {string} rootPath
	 */
	setRootPath: (rootPath) => set({ rootPath }),
	/**
	 * 清理会话信息。
	 */
	clearSession: () => set(createEmptySessionState()),
}));

export default useSessionStore;
